import { BizAppConfig } from 'config/application.manifest';
import { ensureValidAccessToken, getAccessToken } from '@/services/oauth2';
import { createAppRequest, createRequest } from "@/services/request";

const request = createAppRequest();
const metaPlatformRequest = createRequest("https://mp.zhiyueyun.com");

export type OpenApplicationType =
  | "INTERNAL"
  | "SELF_BUILT"
  | "PERSONAL"
  | "SAAS";
export type OpenInternalAccessMode = "ALL_TENANTS" | "SELECTED_TENANTS";
export type OpenAuthorizationStatus = "ACTIVE" | "REVOKED";

export interface OpenApplication {
  applicationId: string;
  applicationType: OpenApplicationType;
  name: string;
  ownerTenantUuid: string;
  requestedScopes: string[];
  callbackUris: string[];
  enabled: boolean;
  applicationEnabled: boolean;
  credentialEnabled: boolean;
  accessKey: string;
  secret?: string;
  apiKey?: string;
  orgId?: string;
  createdByUserId: string;
  tokenLifetime: number;
  accessMode?: OpenInternalAccessMode;
  policyEnabled?: boolean;
  selectedTenantUuids?: string[];
  blockedTenantUuids?: string[];
  grantedScopes: string[];
  authorizationCount: number;
  authorizationId?: string;
  createdAt: string;
}

export interface OpenApplicationPage {
  list: OpenApplication[];
  total: number;
  pageNo: number;
  pageSize: number;
}

export interface AuthorizedSaasApplication extends OpenApplication {
  authorizationId: string;
}

export interface AuthorizedSaasApplicationPage {
  list: AuthorizedSaasApplication[];
  total: number;
  pageNo: number;
  pageSize: number;
}

export interface OpenApplicationInput {
  name: string;
  tokenLifetime: number;
  requestedScopes: string[];
  callbackUris: string[];
}

export interface OpenPersonalApplicationInput {
  name: string;
  tokenLifetime: number;
  requestedScopes: string[];
}

export interface OpenInternalApplicationInput {
  name: string;
  tokenLifetime: number;
  accessMode: OpenInternalAccessMode;
  selectedTenantUuids?: string[];
}

export interface OpenSaasCustomer {
  authorizationId: string;
  tenantUuid: string;
  tenantName: string;
  organizationUuid: string;
  organizationName: string;
  grantedScopes: string[];
  status: OpenAuthorizationStatus;
  authorizedAt: string;
  revokedAt: string | null;
}

export interface OpenCallLogStats {
  totalCalls: number;
  successCalls: number;
  failedCalls: number;
  successRate: number;
  avgDurationMs: number;
  clientErrorCalls: number;
  serverErrorCalls: number;
  slowCalls: number;
}

export interface OpenCallLog {
  uuid: string;
  createdAt: string;
  programmaticAccountId: string;
  applicationId: string;
  applicationName: string | null;
  accessBasisType: "POLICY" | "AUTHORIZATION";
  accessBasisId: string;
  tenantUuid: string;
  memberUuid: string | null;
  requiredScope: string | null;
  operation:
    | "token_exchange"
    | "context_resolve"
    | "api_gateway_forward"
    | "installation_exchange";
  httpMethod: string;
  requestPath: string;
  statusCode: number;
  durationMs: number;
  requestId: string | null;
  clientIp: string | null;
  userAgent: string | null;
  success: boolean;
  failureReason: string | null;
}

export interface OpenCallTrendPoint {
  time: string;
  totalCalls: number;
  successCalls: number;
  failedCalls: number;
  avgDurationMs: number;
}

export interface OpenWorkbenchOverview {
  totalApplications: number;
  activeApplications: number;
  publishedCapabilities: number;
  totalCalls: number;
  todayCalls: number;
  callsLast24Hours: number;
  failedCallsLast24Hours: number;
  successRateLast24Hours: number;
  avgDurationLast24Hours: number;
  operationDistribution: Array<{
    operation: OpenCallLog["operation"];
    totalCalls: number;
    failedCalls: number;
  }>;
  methodDistribution: Array<{
    method: string;
    totalCalls: number;
    failedCalls: number;
  }>;
  statusDistribution: Array<{
    statusGroup: "2xx" | "3xx" | "4xx" | "5xx";
    totalCalls: number;
  }>;
  latencyDistribution: Array<{
    latencyGroup: "under100" | "100to499" | "500to999" | "over999";
    totalCalls: number;
  }>;
  applicationCallDistribution: Array<{
    applicationId: string;
    applicationName: string;
    applicationType: OpenApplicationType;
    totalCalls: number;
    successCalls: number;
    failedCalls: number;
    successRate: number;
  }>;
  recentCalls: OpenCallLog[];
  trendData: OpenCallTrendPoint[];
}

export interface OpenApiCatalogField {
  name: string;
  location: "path" | "query" | "header" | "body" | "response";
  type: string;
  required: boolean;
  description: string;
}

export interface OpenApiCatalogOperation {
  operationId: string;
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  summary: string;
  description: string;
  tags: string[];
  authentication: "none" | "bearer" | "accessKey";
  resourceCode: string;
  requiredScopes: string[];
  /** false = not selectable for personal integrations / Agent skill catalog. Default true. */
  personalAccess?: boolean;
  rateLimitPerMinute: number;
  requestFields: OpenApiCatalogField[];
  responseFields: OpenApiCatalogField[];
  requestExample: unknown;
  responseExample: unknown;
  successStatus: number;
  errors: Array<{ status: number; description: string }>;
}

export interface OpenApiCatalogService {
  serviceCode: string;
  name: string;
  description: string;
  version: string;
  baseUrl: string;
  owner: string;
  publishedAt: string;
  operations: OpenApiCatalogOperation[];
}

export interface OpenApiCatalogServicePage {
  list: OpenApiCatalogService[];
  total: number;
  pageNo: number;
  pageSize: number;
}

export interface OpenApiCatalogServiceSummary {
  serviceCode: string;
  name: string;
  operationCount: number;
}

export interface OpenApiDocumentNode {
  id: string;
  parentId: string | null;
  title: string;
  kind: "guide" | "group" | "operation";
  order: number;
  keywords: string[];
  sectionId?: string;
  operationId?: string;
}

export interface OpenApiGuideSection {
  id: string;
  title: string;
  level: 2 | 3;
  markdown: string;
}

export interface OpenApiCatalogGuide {
  serviceCode: string;
  version: string;
  title: string;
  markdown: string;
  sections: OpenApiGuideSection[];
  navigation: OpenApiDocumentNode[];
}

export interface OpenSkillCapability {
  operationId: string;
  manifestVersion: string;
  category: string;
  intents: string[];
  keywords: string[];
  scenarios: string[];
  riskLevel: "low" | "medium" | "high";
  confirmationRequired: boolean;
  serviceCode: string;
  method: OpenApiCatalogOperation["method"];
  path: string;
  summary: string;
  description: string;
  tags: string[];
  authentication: OpenApiCatalogOperation["authentication"];
  resourceCode: string;
  requiredScopes: string[];
  requestFields: OpenApiCatalogField[];
  responseFields: OpenApiCatalogField[];
  requestExample: unknown;
  responseExample: unknown;
  available: boolean;
}

export interface OpenSkill {
  skillCode: string;
  name: string;
  description: string;
  version: string;
  category: string;
  categories: string[];
  markdownContent: string;
  manifest: {
    integration: {
      credentialFile: string;
      manifestCacheDirectory: string;
      tokenUrl: string;
      capabilitiesUrl: string;
      guideUrlTemplate: string;
      manifestUrl: string;
      gatewayUrl: string;
      instructions: string[];
    };
  };
  capabilities: OpenSkillCapability[];
  availableCapabilityCount: number;
  totalCapabilityCount: number;
  hasPersonalApplication: boolean;
  downloadUrl: string;
  isSystem: boolean;
  packageSize: number | null;
  updatedAt: string;
}

export interface OpenSkillPage {
  list: OpenSkill[];
  total: number;
  pageNo: number;
  pageSize: number;
}

export interface SkillDomainDefinition {
  domain: string;
  name: string;
  description: string;
  serviceCodes: string[];
}

export interface UnifiedSkill {
  skillCode: 'zhiyue-unified';
  name: string;
  description: string;
  version: string;
  minCliVersion: string;
  artifact: { fileName: string; size: number; sha256: string };
  updatedAt: string;
}

export interface OpenApiResourcePermission {
  code: string;
  actions: string[];
  resourceType: "API";
}

export interface BindingContext {
  requestId: string;
  expiresAt: string;
  callbackUri: string;
  state: string;
  appName: string;
  scopes: string[];
}

export interface SaasInstallationContext {
  applicationId: string;
  applicationName: string;
  requestedScopes: string[];
  callbackUris: string[];
}

export interface ManagedOrganization {
  tenantUuid: string;
  tenantName: string;
  organizationUuid: string;
  organizationName: string;
}

export interface OpenApplicationMenuAccess {
  openPlatformInternalApplications: boolean;
  openPlatformSelfBuiltApplications: boolean;
  openPlatformPersonalApplications: boolean;
  openPlatformSaasApplications: boolean;
  openPlatformAuthorizedSaasApplications: boolean;
  openPlatformSaasUserAuthorizations: boolean;
}

export interface SaasUserAuthorizationMember {
  memberUuid: string;
  name?: string;
  email?: string;
  phone?: string;
  status?: string;
  authorized: boolean;
  authorizationSource: "SELF" | "ADMIN" | null;
  authorizedByMemberUuid: string | null;
  authorizedByMemberName: string | null;
  authorizedAt: string | null;
}

export interface SaasUserAuthorizationMemberPage {
  list: SaasUserAuthorizationMember[];
  total: number;
  pageNo: number;
  pageSize: number;
  authorizedCount: number;
  unauthorizedCount: number;
}

const listApplications = (
  resource: string,
  params: { pageNo?: number; pageSize?: number; keyword?: string }
) =>
  request<OpenApplicationPage>(`/bff/open-platform/${resource}`, {
    method: "GET",
    params,
  });

export const listInternalApplications = (params: {
  pageNo?: number;
  pageSize?: number;
  keyword?: string;
}) => listApplications("internal-applications", params);

export const listSelfBuiltApplications = (params: {
  pageNo?: number;
  pageSize?: number;
  keyword?: string;
}) => listApplications("self-built-applications", params);

export const listPersonalApplications = (params: {
  pageNo?: number;
  pageSize?: number;
  keyword?: string;
}) => listApplications("personal-applications", params);

export const listSaasApplications = (params: {
  pageNo?: number;
  pageSize?: number;
  keyword?: string;
}) => listApplications("saas-applications", params);

export const listAuthorizedSaasApplications = (params: {
  pageNo?: number;
  pageSize?: number;
  keyword?: string;
}) =>
  request<AuthorizedSaasApplicationPage>(
    "/bff/open-platform/authorized-saas-applications",
    { method: "GET", params }
  );

export const createInternalApplication = (data: OpenInternalApplicationInput) =>
  request<OpenApplication>("/bff/open-platform/internal-applications", {
    method: "POST",
    data,
  });

export const createSelfBuiltApplication = (data: OpenApplicationInput) =>
  request<OpenApplication>("/bff/open-platform/self-built-applications", {
    method: "POST",
    data,
  });

export const createPersonalApplication = (data: OpenPersonalApplicationInput) =>
  request<OpenApplication>("/bff/open-platform/personal-applications", {
    method: "POST",
    data,
  });

export const createSaasApplication = (data: OpenApplicationInput) =>
  request<OpenApplication>("/bff/open-platform/saas-applications", {
    method: "POST",
    data,
  });

export const updateOpenApplication = (
  applicationId: string,
  data: {
    name?: string;
    requestedScopes?: string[];
    callbackUris?: string[];
    enabled?: boolean;
  }
) =>
  request<OpenApplication>(`/bff/open-platform/applications/${applicationId}`, {
    method: "PATCH",
    data,
  });

export const rotateOpenApplicationSecret = (
  applicationId: string,
  data: { tokenLifetime?: number }
) =>
  request<OpenApplication>(
    `/bff/open-platform/applications/${applicationId}/secret-rotation`,
    { method: "POST", data }
  );

export const updateOpenApplicationCredentialStatus = (
  applicationId: string,
  enabled: boolean
) =>
  request<OpenApplication>(
    `/bff/open-platform/applications/${applicationId}/credential`,
    { method: "PATCH", data: { enabled } }
  );

export const deleteOpenApplication = (applicationId: string) =>
  request<void>(`/bff/open-platform/applications/${applicationId}`, {
    method: "DELETE",
  });

export const updateInternalApplicationPolicy = (
  applicationId: string,
  data: {
    accessMode: OpenInternalAccessMode;
    selectedTenantUuids?: string[];
    enabled: boolean;
  }
) =>
  request<OpenApplication>(
    `/bff/open-platform/internal-applications/${applicationId}/policy`,
    { method: "PATCH", data }
  );

export const updateInternalApplicationPermissions = (
  applicationId: string,
  resources: OpenApiResourcePermission[]
) =>
  request<OpenApplication>(
    `/bff/open-platform/internal-applications/${applicationId}/permissions`,
    { method: "PATCH", data: { resources } }
  );

export const getOpenApplicationPermissions = (applicationId: string) =>
  request<OpenApiResourcePermission[]>(
    `/bff/open-platform/applications/${applicationId}/permissions`,
    { method: "GET" }
  );

export const blockInternalApplicationTenant = (
  applicationId: string,
  tenantUuid: string
) =>
  request<void>(
    `/bff/open-platform/internal-applications/${applicationId}/tenant-exceptions`,
    { method: "POST", data: { tenantUuid } }
  );

export const unblockInternalApplicationTenant = (
  applicationId: string,
  tenantUuid: string
) =>
  request<void>(
    `/bff/open-platform/internal-applications/${applicationId}/tenant-exceptions/${encodeURIComponent(
      tenantUuid
    )}`,
    { method: "DELETE" }
  );

export const listSaasCustomers = (
  applicationId: string,
  params: { pageNo?: number; pageSize?: number }
) =>
  request<{
    list: OpenSaasCustomer[];
    total: number;
    pageNo: number;
    pageSize: number;
  }>(`/bff/open-platform/saas-applications/${applicationId}/customers`, {
    method: "GET",
    params,
  });

export const revokeAuthorizedSaasApplication = (authorizationId: string) =>
  request<void>(
    `/bff/open-platform/authorized-saas-applications/${authorizationId}`,
    { method: "DELETE" }
  );

export const listSaasUserAuthorizationMembers = (
  authorizationId: string,
  params: { pageNo: number; pageSize: number }
) =>
  request<SaasUserAuthorizationMemberPage>(
    `/bff/open-platform/saas-user-authorizations/${encodeURIComponent(
      authorizationId
    )}/members`,
    { method: "GET", params }
  );

export const grantSaasUserAuthorizations = (
  authorizationId: string,
  memberUuids: string[]
) =>
  request<void>(
    `/bff/open-platform/saas-user-authorizations/${encodeURIComponent(
      authorizationId
    )}/grants`,
    { method: "POST", data: { memberUuids } }
  );

export const revokeSaasUserAuthorizations = (
  authorizationId: string,
  memberUuids: string[]
) =>
  request<void>(
    `/bff/open-platform/saas-user-authorizations/${encodeURIComponent(
      authorizationId
    )}/revocations`,
    { method: "POST", data: { memberUuids } }
  );

export const getSaasInstallationContext = (applicationId: string) =>
  request<SaasInstallationContext>(
    `/open/saas/install/${applicationId}/context`,
    { method: "GET" }
  );

export const listManagedOrganizations = () =>
  request<ManagedOrganization[]>("/open/saas/install/managed-organizations", {
    method: "GET",
  });

export const authorizeSaasInstallation = (data: {
  applicationId: string;
  organizationUuid: string;
  redirectUri: string;
  state: string;
}) =>
  request<{ redirectUri: string }>("/open/saas/install/authorize", {
    method: "POST",
    data,
  });

export const getOpenWorkbenchOverview = () =>
  request<OpenWorkbenchOverview>("/bff/open-platform/workbench/overview", {
    method: "GET",
  });

export const getOpenApplicationMenuAccess = () =>
  request<OpenApplicationMenuAccess>(
    "/bff/open-platform/application-menu-access",
    { method: "GET" }
  );

export const listOpenCallLogs = (params?: {
  accountId?: string;
  keyword?: string;
  operation?: string;
  success?: boolean;
  startTime?: string;
  endTime?: string;
  pageNo?: number;
  pageSize?: number;
}) =>
  request<{
    list: OpenCallLog[];
    total: number;
    pageNo: number;
    pageSize: number;
    publicUrl: string;
    todayCalls: number;
    stats: OpenCallLogStats;
  }>("/bff/open-platform/workbench/call-logs", { method: "GET", params });

export const listOpenApiCatalogServices = () =>
  request<OpenApiCatalogService[]>("/bff/open-platform/catalog/services", {
    method: "GET",
  });

export const listOpenApiCatalogServiceSummaries = () =>
  request<OpenApiCatalogServiceSummary[]>(
    '/bff/open-platform/catalog/service-summaries',
    { method: 'GET' },
  );

export const listOpenApiCatalogServicesPage = (params?: {
  keyword?: string;
  pageNo?: number;
  pageSize?: number;
}) =>
  request<OpenApiCatalogServicePage>(
    "/bff/open-platform/catalog/services/page",
    {
      method: "GET",
      params,
    }
  );

export const getOpenApiCatalogService = (serviceCode: string) =>
  request<OpenApiCatalogService>(
    `/bff/open-platform/catalog/services/${encodeURIComponent(serviceCode)}`,
    { method: "GET" }
  );

export const getOpenApiCatalogDocument = (serviceCode: string) =>
  request<Record<string, unknown>>(
    `/bff/open-platform/catalog/services/${encodeURIComponent(
      serviceCode
    )}/openapi.json`,
    { method: "GET" }
  );

export const getOpenApiCatalogGuide = (serviceCode: string) =>
  request<OpenApiCatalogGuide>(
    `/bff/open-platform/catalog/services/${encodeURIComponent(
      serviceCode
    )}/guide`,
    { method: "GET" }
  );

export const listOpenSkills = (params?: {
  keyword?: string;
  category?: string;
  pageNo?: number;
  pageSize?: number;
}) =>
  request<OpenSkillPage>("/bff/open-platform/skills", {
    method: "GET",
    params,
  });

export const getOpenSkill = (skillCode: string) =>
  request<OpenSkill>(
    `/bff/open-platform/skills/${encodeURIComponent(skillCode)}`,
    { method: "GET" }
  );

export const downloadOpenSkill = (skillCode: string) =>
  request<Blob>(
    `/bff/open-platform/skills/${encodeURIComponent(skillCode)}/download`,
    { method: "GET", responseType: "blob" }
  );

const buildSkillFormData = ({
  name,
  description,
  version,
  category,
  file,
}: {
  name: string;
  description: string;
  version: string;
  category: string;
  file: File;
}) => {
  const data = new FormData();
  data.append("name", name);
  data.append("description", description);
  data.append("version", version);
  data.append("category", category);
  data.append("file", file);
  return data;
};

export const createOpenSkill = (input: {
  name: string;
  description: string;
  version: string;
  category: string;
  file: File;
}) =>
  request<OpenSkill>("/bff/open-platform/skills", {
    method: "POST",
    data: buildSkillFormData(input),
    skipErrorHandler: true,
  });

export const uploadOpenSkill = (
  skillCode: string,
  input: {
    name: string;
    description: string;
    version: string;
    category: string;
    file: File;
  }
) =>
  request<OpenSkill>(
    `/bff/open-platform/skills/${encodeURIComponent(skillCode)}/upload`,
    {
      method: "POST",
      data: buildSkillFormData(input),
      skipErrorHandler: true,
    }
  );

export const disableOpenSkill = (skillCode: string) =>
  request<void>(
    `/bff/open-platform/skills/${encodeURIComponent(skillCode)}/disable`,
    { method: "POST", skipErrorHandler: true }
  );

export const listSkillDomains = () =>
  request<SkillDomainDefinition[]>('/bff/open-platform/skill-domains', {
    method: 'GET',
  });

export const createSkillDomain = (data: SkillDomainDefinition) =>
  request<SkillDomainDefinition>('/bff/open-platform/skill-domains', {
    method: 'POST',
    data,
  });

export const updateSkillDomain = (
  domain: string,
  data: Omit<SkillDomainDefinition, 'domain'>,
) =>
  request<SkillDomainDefinition>(
    `/bff/open-platform/skill-domains/${encodeURIComponent(domain)}`,
    { method: 'PUT', data },
  );

export const deleteSkillDomain = (domain: string) =>
  request<void>(
    `/bff/open-platform/skill-domains/${encodeURIComponent(domain)}`,
    { method: 'DELETE' },
  );

export const getUnifiedSkill = () =>
  request<UnifiedSkill | null>('/bff/open-platform/zhiyue-unified', {
    method: 'GET',
  });

export const deleteUnifiedSkill = () =>
  request<void>('/bff/open-platform/zhiyue-unified', { method: 'DELETE' });

async function downloadZipBinary(path: string, data?: { version: string; minCliVersion: string }): Promise<Blob> {
  await ensureValidAccessToken();
  const token = getAccessToken();
  const base = (BizAppConfig.appApiUrl ?? '').replace(/\/$/, '');
  const response = await fetch(`${base}${path}`, {
    method: data ? 'POST' : 'GET',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(data ? { 'Content-Type': 'application/json' } : {}) },
    body: data ? JSON.stringify(data) : undefined,
  });
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (!response.ok) {
    const preview = new TextDecoder().decode(bytes.slice(0, 200));
    throw new Error(`下载失败 HTTP ${response.status}: ${preview || response.statusText}`);
  }
  if (bytes.length < 4 || bytes[0] !== 0x50 || bytes[1] !== 0x4b) {
    throw new Error('下载内容不是有效 ZIP 文件');
  }
  return new Blob([bytes], { type: 'application/zip' });
}

export const downloadUnifiedSkill = () =>
  downloadZipBinary('/bff/open-platform/zhiyue-unified/download');

export const previewUnifiedSkill = (version: string, minCliVersion: string) =>
  downloadZipBinary('/bff/open-platform/zhiyue-unified/preview', { version, minCliVersion });

export const publishUnifiedSkill = (version: string, minCliVersion: string) =>
  request<UnifiedSkill>('/bff/open-platform/zhiyue-unified/publish', {
    method: 'POST',
    data: { version, minCliVersion },
  });

export const getOpenApiOperationDocument = (
  document: Record<string, unknown>,
  operation: Pick<OpenApiCatalogOperation, "method" | "path">
): Record<string, unknown> => {
  const paths = document.paths;
  if (typeof paths !== "object" || paths === null || Array.isArray(paths)) {
    throw new Error("OpenAPI 文档缺少有效的 paths 定义");
  }
  const pathItem = (paths as Record<string, unknown>)[operation.path];
  if (
    typeof pathItem !== "object" ||
    pathItem === null ||
    Array.isArray(pathItem)
  ) {
    throw new Error(`OpenAPI 文档缺少接口路径: ${operation.path}`);
  }
  const method = operation.method.toLowerCase();
  const operationDefinition = (pathItem as Record<string, unknown>)[method];
  if (
    typeof operationDefinition !== "object" ||
    operationDefinition === null ||
    Array.isArray(operationDefinition)
  ) {
    throw new Error(
      `OpenAPI 文档缺少接口定义: ${operation.method} ${operation.path}`
    );
  }
  return {
    ...document,
    paths: { [operation.path]: { [method]: operationDefinition } },
  };
};

export const uploadOpenApiCatalogDocument = ({
  serviceCode,
  document,
  guideMarkdown,
}: {
  serviceCode: string;
  document: Record<string, unknown>;
  guideMarkdown: string;
}) =>
  request<OpenApiCatalogService>(
    `/bff/open-platform/catalog/services/${encodeURIComponent(
      serviceCode
    )}/upload`,
    {
      method: "POST",
      data: { document, guideMarkdown },
      skipErrorHandler: true,
    }
  );

export const parseOpenApiDocument = (
  content: string
): Record<string, unknown> => {
  let document: unknown;
  try {
    document = JSON.parse(content) as unknown;
  } catch {
    throw new Error("文件不是有效的 JSON 文档");
  }
  if (
    typeof document !== "object" ||
    document === null ||
    Array.isArray(document)
  ) {
    throw new Error("OpenAPI 文档必须是 JSON 对象");
  }
  return document as Record<string, unknown>;
};

export const parseOpenApiGuide = (content: string): string => {
  const guideMarkdown = content.replace(/\r\n?/g, "\n").trim();
  if (!guideMarkdown) {
    throw new Error("openapi-guide.md 不能为空");
  }
  return `${guideMarkdown}\n`;
};

export const getBindingContext = (requestId: string) =>
  request<BindingContext>(`/open/self-built/bind/${requestId}/context`, {
    method: "GET",
  });

export const confirmBinding = (requestId: string) =>
  request<{ redirectUri: string }>(
    `/open/self-built/bind/${requestId}/confirm`,
    { method: "POST", data: {} }
  );

export interface OpenTenantListItem {
  tenantUuid: string;
  name: string;
  displayName?: string;
  tenantCode?: string;
}

export interface OpenTenantListResponse {
  list: OpenTenantListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface OpenPlatformMember {
  memberUuid: string;
  name?: string;
  email?: string;
  phone?: string;
  status?: string;
  userId?: string;
  deptIds?: string[];
  nickname?: string;
  position?: string;
  username?: string;
  jobNumber?: string;
  workStatus?: string;
}

export interface OpenPlatformMemberPage {
  list: OpenPlatformMember[];
  total: number;
  pageNo: number;
  pageSize: number;
}

export const listOpenPlatformTestMembers = (params: {
  pageNo: number;
  pageSize: number;
}) =>
  request<OpenPlatformMemberPage>("/bff/open-platform/test/members", {
    method: "GET",
    params,
  });

export const getSaasUserBindingContext = (requestId: string) =>
  request<BindingContext>(`/open/saas/user-bind/${requestId}/context`, {
    method: "GET",
  });

export const confirmSaasUserBinding = (requestId: string) =>
  request<{ redirectUri: string }>(
    `/open/saas/user-bind/${requestId}/confirm`,
    { method: "POST", data: {} }
  );

export const listTenants = (params?: {
  keyword?: string;
  page?: number;
  pageSize?: number;
}) =>
  metaPlatformRequest<OpenTenantListResponse>(
    "/bff/tenant-management/tenants",
    {
      method: "GET",
      params,
    }
  );
